import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Loader, Modal } from 'semantic-ui-react';

import ModalTypes from '../../constants/ModalTypes';
import FixedContainer from '../../containers/FixedContainer';
import StaticContainer from '../../containers/StaticContainer';
import UsersModalContainer from '../../containers/UsersModalContainer';
import ArchiveModalContainer from '../../containers/ArchiveModalContainer';
import UserSettingsModalContainer from '../../containers/UserSettingsModalContainer';
import ProjectAddModalContainer from '../../containers/ProjectAddModalContainer';
import PointSystemModalContainer from '../../containers/PointSystemModalContainer';
import MonthlyReportModalContainer from '../../containers/MonthlyReportModalContainer';
import UnclosedTasksModalContainer from '../../containers/UnclosedTasksModalContainer';
import PointSystemTransferModal from '../PointSystemTransferModal';
import Background from '../Background';
import actions from '../../actions';
import entryActions from '../../entry-actions';
import selectors from '../../selectors';

import styles from './Core.module.scss';

const Core = React.memo(
  ({ isInitializing, isSocketDisconnected, currentModal, currentProject, currentBoard }) => {
    const [t] = useTranslation();
    const dispatch = useDispatch();

    const listTransferPointSystemPrompt = useSelector(
      selectors.selectListTransferPointSystemPrompt,
    );
    const cardTransferPointSystemPrompt = useSelector(
      selectors.selectCardTransferPointSystemPrompt,
    );

    const defaultTitle = useRef(document.title);

    const handleRefresh = useCallback(() => {
      window.location.reload();
    }, []);

    const activePointSystemPrompt = cardTransferPointSystemPrompt || listTransferPointSystemPrompt;
    const isCardPrompt = Boolean(cardTransferPointSystemPrompt);

    const handlePointSystemPromptClose = useCallback(() => {
      if (isCardPrompt) {
        dispatch(actions.clearCardTransferPointSystemPrompt());
        dispatch(actions.clearCardTransferStatus());
      } else {
        dispatch(actions.clearListTransferPointSystemPrompt());
      }
    }, [dispatch, isCardPrompt]);

    const handlePointSystemPromptConfirm = useCallback(() => {
      if (isCardPrompt) {
        dispatch(entryActions.confirmCardTransferPointSystem(true));
      } else {
        dispatch(entryActions.confirmListTransferPointSystem(true));
      }
    }, [dispatch, isCardPrompt]);

    const handlePointSystemPromptRefuse = useCallback(() => {
      if (isCardPrompt) {
        dispatch(entryActions.confirmCardTransferPointSystem(false));
      } else {
        dispatch(entryActions.confirmListTransferPointSystem(false));
      }
    }, [dispatch, isCardPrompt]);

    useEffect(() => {
      let title;
      if (currentProject) {
        title = currentProject.name;

        if (currentBoard) {
          title += ` | ${currentBoard.name}`;
        }
      } else {
        title = defaultTitle.current;
      }

      document.title = title;
    }, [currentProject, currentBoard]);

    return (
      <>
        {isInitializing ? (
          <Loader active size="massive" />
        ) : (
          <>
            {currentProject && currentProject.background && (
              <Background
                type={currentProject.background.type}
                name={currentProject.background.name}
                imageUrl={currentProject.backgroundImage && currentProject.backgroundImage.url}
              />
            )}
            <FixedContainer />
            <StaticContainer />
            {currentModal === ModalTypes.ARCHIVE && <ArchiveModalContainer />}
            {currentModal === ModalTypes.USERS && <UsersModalContainer />}
            {currentModal === ModalTypes.USER_SETTINGS && <UserSettingsModalContainer />}
            {currentModal === ModalTypes.PROJECT_ADD && <ProjectAddModalContainer />}
            {currentModal === ModalTypes.POINT_SYSTEM && <PointSystemModalContainer />}
            {currentModal === ModalTypes.MONTHLY_REPORT && <MonthlyReportModalContainer />}
            {currentModal === ModalTypes.UNCLOSED_TASKS && <UnclosedTasksModalContainer />}
            <PointSystemTransferModal
              open={Boolean(activePointSystemPrompt)}
              mode={
                (activePointSystemPrompt && activePointSystemPrompt.mode) || 'enable_and_apply'
              }
              templateName={
                (activePointSystemPrompt && activePointSystemPrompt.templateName) || ''
              }
              onConfirm={handlePointSystemPromptConfirm}
              onRefuse={handlePointSystemPromptRefuse}
              onClose={handlePointSystemPromptClose}
            />
          </>
        )}
        <Modal
          open={isSocketDisconnected}
          size="tiny"
          closeOnDimmerClick={false}
          closeOnEscape={false}
          className={styles.connectionLostModal}
        >
          <Modal.Content className={styles.connectionLostContent}>
            {t('common.connectionLostToServer')}
          </Modal.Content>
          <Modal.Actions className={styles.connectionLostActions}>
            <Button
              primary
              icon="refresh"
              labelPosition="left"
              content={t('action.refresh')}
              onClick={handleRefresh}
            />
          </Modal.Actions>
        </Modal>
      </>
    );
  },
);

Core.propTypes = {
  isInitializing: PropTypes.bool.isRequired,
  isSocketDisconnected: PropTypes.bool.isRequired,
  currentModal: PropTypes.oneOf(Object.values(ModalTypes)),
  /* eslint-disable react/forbid-prop-types */
  currentProject: PropTypes.object,
  currentBoard: PropTypes.object,
  /* eslint-enable react/forbid-prop-types */
};

Core.defaultProps = {
  currentModal: undefined,
  currentProject: undefined,
  currentBoard: undefined,
};

export default Core;
